// ============================================================================
// Layout — app shell (slice 2 of the redesign rollout).
//
// Top bar (default): 60px sticky glass, candlestick Logo + nav links, a
// decorative search affordance, LIVE pill, alerts bell, user cluster
// (avatar + name + plan badge) that opens the profile dropdown with the
// existing real updateProfile edit form.
//
// Responsive breakpoints per the redesign spec:
//   ≤1080  nav links collapse → hamburger opens a 260px left drawer.
//   ≤980   search hidden, content goes full-width.
//   ≤620   LIVE pill + user-name hidden (avatar/menu stays).
//
// The mobile bottom-nav (added in an earlier session) is kept — phones still
// get one-tap nav for the four most-used pages, and the drawer covers the
// rest (Docs, Admin).
//
// What's unchanged from the previous Layout: useAuth() / updateProfile flow,
// SecurityDetailModal portal mount, ProductTour, ExchangeContext usage,
// trial-expiry banner. Backend logic and router untouched.
// ============================================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useExchange } from '../context/ExchangeContext';
import {
  LogOut, Shield, Star, Menu, X, DollarSign, BookOpen, Zap, BarChart3,
  Phone, Globe, FileText, Calendar, Edit2, Save, Check, Clock, AlertTriangle,
  ChevronDown,
} from 'lucide-react';
import SecurityDetailModal from './SecurityDetailModal';
import NewTradeModal from './NewTradeModal';
import ProductTour from './ProductTour';
import { isLiveData, getDataSource } from '../utils/marketEngine';
import Logo from './ui/Logo';
import TopBarSearch from './ui/TopBarSearch';
import NotificationsBell from './ui/NotificationsBell';

const NAV_ITEMS_BASE = [
  { to: '/dashboard',           label: 'Overview', icon: BarChart3 },
  { to: '/dashboard/signals',   label: 'Signals',  icon: Zap },
  { to: '/dashboard/trading',   label: 'Trading',  icon: DollarSign },
  { to: '/dashboard/favorites', label: 'Watchlist',icon: Star },
  { to: '/dashboard/docs',      label: 'Docs',     icon: BookOpen },
];

export default function Layout() {
  const { user, logout, updateProfile } = useAuth();
  const { config } = useExchange();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedTicker,  setSelectedTicker]  = useState(null);
  // tradePrefill drives the shared NewTradeModal at Layout level. Any
  // descendant page calls `openTrade(prefill)` via the Outlet context to
  // open the modal IN PLACE — no navigation away from the current page.
  // Pages that care about post-trade refresh (TradingPage's positions
  // list) provide an `onTradeSuccess` listener (see openTrade ref below).
  const [tradePrefill,   setTradePrefill]    = useState(null);
  const tradeSuccessRef = useRef(null);
  const openTrade = useCallback((prefill, onSuccess) => {
    tradeSuccessRef.current = onSuccess || null;
    setTradePrefill(prefill || {});
  }, []);
  const closeTrade = useCallback(() => {
    setTradePrefill(null);
    tradeSuccessRef.current = null;
  }, []);
  const handleTradeSuccess = useCallback((result) => {
    try { tradeSuccessRef.current?.(result); } catch { /* listener exploded — not our problem */ }
  }, []);
  const [mobileMenuOpen,  setMobileMenuOpen]  = useState(false);
  const [showUserMenu,    setShowUserMenu]    = useState(false);
  const [editingProfile,  setEditingProfile]  = useState(false);
  const [form,            setForm]            = useState({ name: '', phone: '', country: '', bio: '' });
  const [saving,          setSaving]          = useState(false);
  const [saveError,       setSaveError]       = useState('');
  const [saveOk,          setSaveOk]          = useState(false);
  const userMenuRef = useRef(null);

  // Close drawer on route change so back-button + nav-click both feel right.
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Close dropdowns on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
        setEditingProfile(false);
        setSaveError('');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Esc closes the drawer + dropdown.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setMobileMenuOpen(false);
      setShowUserMenu(false);
      setEditingProfile(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = useCallback(() => { logout(); navigate('/'); }, [logout, navigate]);

  const openUserMenu = () => {
    setForm({ name: user?.name || '', phone: user?.phone || '', country: user?.country || '', bio: user?.bio || '' });
    setSaveError('');
    setSaveOk(false);
    setEditingProfile(false);
    setShowUserMenu(v => !v);
  };
  const startEdit  = () => {
    setForm({ name: user?.name || '', phone: user?.phone || '', country: user?.country || '', bio: user?.bio || '' });
    setSaveError(''); setSaveOk(false); setEditingProfile(true);
  };
  const cancelEdit = () => { setEditingProfile(false); setSaveError(''); };

  const handleSave = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      return setSaveError('Name must be at least 2 characters');
    }
    setSaving(true); setSaveError('');
    try {
      await updateProfile(form);
      setSaveOk(true);
      setEditingProfile(false);
      setTimeout(() => setSaveOk(false), 2500);
    } catch (e) {
      setSaveError(e.message || 'Failed to save');
    }
    setSaving(false);
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Trial expiry banner
  const trialDaysLeft = (() => {
    if (user?.subscription_status !== 'trial' || !user?.trial_ends_at) return null;
    return Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000);
  })();
  const showTrialBanner = trialDaysLeft !== null && trialDaysLeft <= 7;

  // Subscription badge (profile dropdown + drawer)
  const subscriptionBadge = (() => {
    if (user?.role === 'admin') return null;
    const s = user?.subscription_status;
    if (s === 'trial') {
      const d = trialDaysLeft;
      if (d !== null && d <= 0) return { label: 'Trial Expired', color: 'var(--red-soft)', bg: 'var(--red-dim)' };
      return { label: `Trial · ${d}d left`, color: 'var(--amber-soft)', bg: 'var(--amber-dim)' };
    }
    if (s === 'active')    return { label: user.subscription_ends_at ? `Active · ${fmt(user.subscription_ends_at)}` : 'Active · Forever', color: 'var(--green-soft)', bg: 'var(--green-dim)' };
    if (s === 'paused')    return { label: 'Paused',    color: '#fb923c',      bg: 'rgba(249,115,22,0.12)' };
    if (s === 'cancelled') return { label: 'Cancelled', color: 'var(--red-soft)', bg: 'var(--red-dim)' };
    return { label: 'Active', color: 'var(--green-soft)', bg: 'var(--green-dim)' };
  })();

  const navItems = [
    ...NAV_ITEMS_BASE,
    ...(user?.role === 'admin' ? [{ to: '/dashboard/admin', label: 'Admin', icon: Shield }] : []),
  ];

  // Initial for the avatar tile.
  const initial = (user?.name || user?.email || 'U')[0].toUpperCase();
  const planLabel = user?.role === 'admin'
    ? 'Admin · lifetime'
    : (user?.subscription_status === 'trial' && trialDaysLeft != null ? `Trial · ${Math.max(0, trialDaysLeft)}d left` : 'Member');

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 7,
    border: '1px solid var(--border)', background: 'var(--bg-surface)',
    color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', width: '100%', overflowX: 'hidden' }}>
      {/* ─────────────────────────────────── Top bar ─────────────────────── */}
      <nav className="top-nav glass" style={{
        position: 'sticky', top: 0, zIndex: 100,
        height: 60, display: 'flex', alignItems: 'center', gap: 18,
        padding: '0 clamp(14px, 3vw, 28px)',
        borderBottom: '1px solid var(--border)',
        width: '100%', boxSizing: 'border-box', maxWidth: '100vw',
      }}>
        {/* Mobile hamburger — visible ≤1080 via CSS */}
        <button
          className="nav-burger"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open navigation"
          style={{
            display: 'none', width: 36, height: 36, borderRadius: 'var(--r-md)',
            border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)',
            cursor: 'pointer', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Menu size={17} />
        </button>

        <Logo size={18} onClick={() => navigate('/dashboard')} />

        {/* Vertical divider + nav-link group — both hide at ≤1080 */}
        <span className="topnav-links nav-divider" style={{ width: 1, height: 22, background: 'var(--border)', flexShrink: 0 }} />
        <div className="topnav-links" style={{ display: 'flex', gap: 2 }}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              style={({ isActive }) => ({
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '8px 13px', borderRadius: 'var(--r-md)',
                fontSize: 12.5, fontWeight: 600, textDecoration: 'none',
                background: isActive ? 'var(--accent-dim)' : 'transparent',
                color:      isActive ? 'var(--accent-soft)' : 'var(--text-secondary)',
                whiteSpace: 'nowrap',
                transition: 'background-color var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
              })}
            >
              <item.icon size={14} strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
        </div>

        <span style={{ flex: 1 }} />

        {/* Real ticker search — hides ≤980. Opens SecurityDetailModal via
            the existing setSelectedTicker portal. ⌘K focuses the input. */}
        <TopBarSearch onPick={setSelectedTicker} />

        {/* LIVE pill — hides ≤620 */}
        <span className="nav-live live" style={{
          display: 'inline-flex', alignItems: 'center',
          fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
          padding: '4px 10px', borderRadius: 'var(--r-pill)',
          background: isLiveData() ? 'var(--green-dim)' : 'var(--amber-dim)',
          color:      isLiveData() ? 'var(--green)'    : 'var(--amber)',
          border: `1px solid ${isLiveData() ? 'rgba(34,197,94,0.22)' : 'rgba(245,158,11,0.22)'}`,
          flexShrink: 0,
        }}>
          {isLiveData() ? 'LIVE' : 'SIM'}
        </span>

        {/* Notifications — portfolio heat warnings, expiring options, recent trades. */}
        <NotificationsBell />

        {/* User cluster — avatar + name + plan + chevron */}
        <div ref={userMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={openUserMenu}
            aria-haspopup="menu"
            aria-expanded={showUserMenu}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '4px 6px 4px 4px', borderRadius: 'var(--r-md)',
              border: 'none', background: 'transparent', cursor: 'pointer',
            }}
          >
            <div style={{
              width: 32, height: 32, borderRadius: 'var(--r-md)',
              background: 'var(--grad-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 800, color: '#fff', flexShrink: 0,
              boxShadow: '0 4px 14px var(--accent-glow)',
            }}>{initial}</div>
            <div className="nav-user" style={{ lineHeight: 1.2, textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{user?.name || 'User'}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{planLabel}</div>
            </div>
            <ChevronDown size={13} color="var(--text-muted)" style={{ flexShrink: 0 }} />
          </button>

          {showUserMenu && (
            <div role="menu" style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 300,
              width: 'min(320px, calc(100vw - 24px))',
              background: 'var(--bg-elevated)',
              borderRadius: 'var(--r-lg)',
              border: '1px solid var(--border-strong)',
              boxShadow: 'var(--shadow-lift)', overflow: 'hidden',
              animation: 'fadeIn var(--dur-fast) var(--ease-out) both',
            }}>
              {!editingProfile ? (
                <>
                  {/* Profile header */}
                  <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: 'var(--r-md)',
                        background: 'var(--grad-accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, fontWeight: 800, color: '#fff',
                        boxShadow: '0 3px 12px var(--accent-glow)', flexShrink: 0,
                      }}>{initial}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                          <span style={{
                            fontSize: 10, padding: '2px 7px', borderRadius: 'var(--r-pill)', fontWeight: 700,
                            background: user?.role === 'admin' ? 'var(--red-dim)' : 'var(--accent-dim)',
                            color:      user?.role === 'admin' ? 'var(--red-soft)' : 'var(--accent-soft)',
                          }}>
                            {user?.role === 'admin' ? 'Admin' : 'Member'}
                          </span>
                          {subscriptionBadge && (
                            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 'var(--r-pill)', fontWeight: 700, background: subscriptionBadge.bg, color: subscriptionBadge.color }}>
                              {subscriptionBadge.label}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Info rows */}
                  <div style={{ padding: '8px 0' }}>
                    {[
                      { Icon: Phone,    label: user?.phone   || 'Phone not set',   muted: !user?.phone },
                      { Icon: Globe,    label: user?.country || 'Country not set', muted: !user?.country },
                      { Icon: FileText, label: user?.bio     || 'Bio not set',     muted: !user?.bio },
                      { Icon: Calendar, label: `Joined ${fmt(user?.created_at)}`,   muted: false },
                    ].map(({ Icon, label, muted }, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 16px' }}>
                        <Icon size={12} color={muted ? 'var(--text-muted)' : 'var(--accent)'} style={{ flexShrink: 0 }} />
                        <span style={{
                          fontSize: 12, color: muted ? 'var(--text-muted)' : 'var(--text-secondary)',
                          fontStyle: muted ? 'italic' : 'normal',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{label}</span>
                      </div>
                    ))}
                  </div>

                  {saveOk && (
                    <div style={{
                      margin: '0 12px 8px', padding: '6px 10px', borderRadius: 'var(--r-sm)',
                      background: 'var(--green-dim)', border: '1px solid rgba(16,185,129,0.2)',
                      color: 'var(--green)', fontSize: 11,
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      <Check size={11} /> Profile updated
                    </div>
                  )}

                  <div style={{ padding: '8px 12px 12px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8 }}>
                    <button onClick={startEdit} style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                      padding: '9px 0', borderRadius: 'var(--r-sm)',
                      border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)',
                      cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                    }}>
                      <Edit2 size={11} /> Edit Profile
                    </button>
                    <button onClick={handleLogout} style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                      padding: '9px 0', borderRadius: 'var(--r-sm)',
                      border: 'none', background: 'var(--red-dim)', color: 'var(--red-soft)',
                      cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                    }}>
                      <LogOut size={11} /> Sign Out
                    </button>
                  </div>
                </>
              ) : (
                // Edit profile form
                <>
                  <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Edit Profile</span>
                    <button onClick={cancelEdit} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}><X size={14} /></button>
                  </div>
                  <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Full Name *</label>
                      <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="Full Name" />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Phone</label>
                        <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={inputStyle} placeholder="+1 234 567" type="tel" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Country</label>
                        <input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} style={inputStyle} placeholder="e.g. US" />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Bio</label>
                      <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                        style={{ ...inputStyle, minHeight: 64, resize: 'vertical', lineHeight: 1.5 }}
                        placeholder="A bit about you..." />
                    </div>
                    {saveError && (
                      <div style={{ padding: '6px 10px', borderRadius: 'var(--r-sm)', background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.2)', color: 'var(--red-soft)', fontSize: 11 }}>{saveError}</div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={cancelEdit} style={{ flex: 1, padding: '8px 0', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>Cancel</button>
                      <button onClick={handleSave} disabled={saving} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '8px 0', borderRadius: 'var(--r-sm)', border: 'none', background: 'var(--grad-accent)', color: '#fff', cursor: saving ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}>
                        <Save size={11} /> {saving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Trial banner — appears below the nav when ≤7 days remaining */}
      {showTrialBanner && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '7px 20px', fontSize: 12, fontWeight: 500,
          background: trialDaysLeft <= 2 ? 'var(--red-dim)' : 'var(--amber-dim)',
          borderBottom: `1px solid ${trialDaysLeft <= 2 ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.15)'}`,
          color: trialDaysLeft <= 2 ? 'var(--red-soft)' : 'var(--amber-soft)',
        }}>
          {trialDaysLeft <= 2 ? <AlertTriangle size={13} /> : <Clock size={13} />}
          {trialDaysLeft <= 0
            ? 'Your free trial has expired. Contact an admin to continue access.'
            : `Your free trial expires in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}.`}
        </div>
      )}

      {/* Mobile drawer — 260px sliding left panel, opens on hamburger tap */}
      {mobileMenuOpen && (
        <div
          onMouseDown={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(5,8,15,0.72)', backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            className="glass"
            style={{
              position: 'absolute', top: 0, left: 0, bottom: 0, width: 280,
              maxWidth: 'calc(100vw - 48px)',
              padding: 18, borderRight: '1px solid var(--border)',
              display: 'flex', flexDirection: 'column', gap: 4,
              animation: 'fadeUp 0.2s var(--ease-out) both',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px 18px' }}>
              <Logo size={18} />
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close navigation"
                style={{
                  width: 32, height: 32, borderRadius: 'var(--r-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text-muted)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              ><X size={15} /></button>
            </div>

            {/* User summary at the top of the drawer */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 11,
              padding: '12px 8px 14px',
              borderBottom: '1px solid var(--border-subtle)',
              marginBottom: 8,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 'var(--r-md)',
                background: 'var(--grad-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 800, color: '#fff', flexShrink: 0,
              }}>{initial}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{planLabel}</div>
              </div>
            </div>

            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/dashboard'}
                onClick={() => setMobileMenuOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 11,
                  padding: '12px 12px', borderRadius: 'var(--r-md)',
                  fontSize: 14, fontWeight: 600, textDecoration: 'none',
                  background: isActive ? 'var(--accent-dim)' : 'transparent',
                  color:      isActive ? 'var(--accent-soft)' : 'var(--text-secondary)',
                })}
              >
                <item.icon size={17} strokeWidth={2} />
                {item.label}
              </NavLink>
            ))}

            <span style={{ flex: 1 }} />

            <button onClick={handleLogout} style={{
              display: 'flex', alignItems: 'center', gap: 11,
              padding: '11px 12px', borderRadius: 'var(--r-md)',
              border: 'none', background: 'var(--red-dim)', color: 'var(--red-soft)',
              cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
            }}>
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main
        className="main-content"
        style={{
          padding: 'calc(var(--section-gap) - 4px) clamp(14px, 3vw, 32px) 96px',
          maxWidth: 1440, margin: '0 auto',
          width: '100%', boxSizing: 'border-box',
        }}
      >
        <Outlet context={{ setSelectedTicker, openTrade }} />
      </main>

      {selectedTicker && <SecurityDetailModal ticker={selectedTicker} onClose={() => setSelectedTicker(null)} />}
      {tradePrefill !== null && (
        <NewTradeModal
          prefill={tradePrefill}
          onClose={closeTrade}
          onSuccess={handleTradeSuccess}
        />
      )}

      <ProductTour />

      <footer style={{
        padding: '10px 24px 14px',
        paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 10, color: 'var(--text-faint)',
        flexWrap: 'wrap', gap: 8,
      }}>
        <span>Stokora · {config.flag} {config.label} · {config.tradingHours}</span>
        <span>
          {isLiveData() ? `${getDataSource()} · delayed up to 15 min` : 'Simulated mode'}
          {' · Not financial advice'}
        </span>
      </footer>

      {/* Mobile bottom-nav — kept from the prior session so phones still get
          one-tap nav for the four most-used pages. Docs + Admin live in the
          drawer to keep this bar thumb-friendly. */}
      <nav
        className="mobile-bottom-nav glass"
        aria-label="Primary navigation"
        style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 90,
          display: 'flex', justifyContent: 'space-around', alignItems: 'center',
          padding: '8px 4px calc(8px + env(safe-area-inset-bottom)) 4px',
          borderTop: '1px solid var(--border)',
        }}
      >
        {[
          { to: '/dashboard',           label: 'Home',    icon: BarChart3 },
          { to: '/dashboard/signals',   label: 'Signals', icon: Zap },
          { to: '/dashboard/trading',   label: 'Trade',   icon: DollarSign },
          { to: '/dashboard/favorites', label: 'Watch',   icon: Star },
        ].map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/dashboard'}
            style={({ isActive }) => ({
              flex: 1, maxWidth: 80,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              padding: '6px 4px', textDecoration: 'none',
              color: isActive ? 'var(--accent-soft)' : 'var(--text-muted)',
              fontSize: 10, fontWeight: 600,
              position: 'relative',
            })}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span style={{
                    position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                    width: 24, height: 2, borderRadius: 'var(--r-pill)',
                    background: 'var(--grad-accent)',
                  }} />
                )}
                <item.icon size={18} strokeWidth={isActive ? 2.4 : 2} />
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Responsive rules (spec breakpoints 1080 / 980 / 620). Bottom nav
          shows ≤768 to match the user's existing mobile-first behaviour. */}
      <style>{`
        @media (max-width: 1080px) {
          .topnav-links { display: none !important; }
          .nav-burger { display: flex !important; }
        }
        @media (max-width: 980px) {
          .topnav-search { display: none !important; }
        }
        @media (max-width: 768px) {
          .mobile-bottom-nav { display: flex !important; }
          .main-content { padding-bottom: 96px !important; }
        }
        @media (min-width: 769px) {
          .mobile-bottom-nav { display: none !important; }
        }
        @media (max-width: 620px) {
          .nav-live { display: none !important; }
          .nav-user { display: none !important; }
          .nav-bell { display: none !important; }
        }
      `}</style>
    </div>
  );
}
